import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Box, type SxProps, type Theme } from '@mui/material';

interface ScrollRevealProps {
  children: React.ReactNode;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
  sx?: SxProps<Theme>;
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  delay = 0,
  direction = 'up',
  sx,
}) => {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <Box sx={sx}>{children}</Box>;
  }

  const getInitialPosition = () => {
    switch (direction) {
      case 'up':
        return { y: 50, x: 0 };
      case 'down':
        return { y: -50, x: 0 };
      case 'left':
        return { y: 0, x: 50 };
      case 'right':
        return { y: 0, x: -50 };
      default:
        return { y: 50, x: 0 };
    }
  };

  const initial = getInitialPosition();

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, ...initial }}
      whileInView={{ opacity: 1, y: 0, x: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{
        duration: 0.6,
        delay,
        ease: [0.25, 0.4, 0.25, 1],
      }}
      sx={sx}
    >
      {children}
    </Box>
  );
};

export default ScrollReveal;
